// Estatísticas na tela (tarefa 5.4): o bloco "Estatísticas" do painel do agente, os números de
// custo e de tokens que o Meu dia mostra, e os selos do consumo do plano (desatualizado e sem cota).
//
// O cálculo é puro (sem DOM, testado em tests/stats-web.test.ts); só as funções *Html tocam o i18n.
// Custo que a CLI não informou aparece como '—': nunca se estima aqui.

import type { Agent, UsageInfo } from '../core/types.js';
import { localeTag, plural, t } from './i18n.js';

/** Uma leitura do consumo com mais de meia hora é dada como desatualizada. */
export const DESATUALIZADO_MS = 30 * 60_000;

/** Quantas ferramentas a lista do painel mostra (as mais usadas). */
const FERRAMENTAS_NA_LISTA = 6;

/** A leitura do consumo está velha (mais de DESATUALIZADO_MS desde que foi feita)? */
export function consumoDesatualizado(usage: { fetchedAt?: number } | undefined, now: number): boolean {
  return usage?.fetchedAt !== undefined && now - usage.fetchedAt > DESATUALIZADO_MS;
}

/** O que o consumo de uma CLI precisa para o anel e os selos (o UsageInfo do core, com campos opcionais). */
type ConsumoDaCli = { windows: ReadonlyArray<{ label: string; percent: number }>; fetchedAt?: number; semCota?: boolean };

/** O que o anel do celular mostra: o maior uso de 5 h e o estado da CLI que o deu. */
export interface AnelDoConsumo {
  pct?: number;
  desatualizado: boolean;
  semCota: boolean;
}

/**
 * O maior uso da janela de 5 h entre as CLIs (mesma regra de maiorConsumo5h, em mobile.ts), com o
 * estado da CLI de onde ele veio: o anel mostra o número e diz se ele está velho ou sem cota.
 */
export function consumoDoAnel(providers: ReadonlyArray<{ usage?: ConsumoDaCli }>, now: number): AnelDoConsumo {
  let melhor: { pct: number; usage: ConsumoDaCli | undefined } | undefined;
  for (const p of providers) {
    for (const w of p.usage?.windows ?? []) {
      if (w.label !== '5h') continue;
      const pct = Math.round(Math.max(0, Math.min(100, w.percent)));
      if (!melhor || pct > melhor.pct) melhor = { pct, usage: p.usage };
    }
  }
  if (!melhor) return { desatualizado: false, semCota: false };
  return { pct: melhor.pct, desatualizado: consumoDesatualizado(melhor.usage, now), semCota: melhor.usage?.semCota === true };
}

/** Selos do consumo de uma CLI: 'desatualizado' e 'sem cota', quando valem. Vazio quando nenhum. */
export function seloConsumoHtml(usage: { fetchedAt: number; semCota?: boolean } | undefined, now: number): string {
  if (!usage) return '';
  const semCota = usage.semCota === true ? `<span class="uso-selo semcota" title="${escape(t('stats.sem_cota_titulo'))}">${t('stats.sem_cota')}</span>` : '';
  const velho = consumoDesatualizado(usage, now) ? `<span class="uso-selo desat" title="${escape(t('stats.desatualizado_titulo'))}">${t('stats.desatualizado')}</span>` : '';
  return semCota || velho ? `<span class="uso-selos">${semCota}${velho}</span>` : '';
}

/** Custo em dólar, no formato do idioma. null/undefined = a CLI não informou: '—'. Abaixo de um centavo, '< US$ 0,01'. */
export function custoTexto(usd: number | null | undefined): string {
  if (usd === null || usd === undefined || !Number.isFinite(usd)) return '—';
  const moeda = new Intl.NumberFormat(localeTag(), { style: 'currency', currency: 'USD' });
  if (usd > 0 && usd < 0.01) return `< ${moeda.format(0.01)}`;
  return moeda.format(usd);
}

/** Linhas alteradas no formato '+12 −3'. Sem dado: '—'. */
export function linhasTexto(added: number | undefined, removed: number | undefined): string {
  if (added === undefined && removed === undefined) return '—';
  const n = (v: number | undefined) => (v ?? 0).toLocaleString(localeTag());
  return `+${n(added)} −${n(removed)}`;
}

/** Número de tokens, ou '—' quando a CLI não informou. */
export function tokensTexto(valor: number | undefined): string {
  return valor === undefined ? '—' : valor.toLocaleString(localeTag());
}

/** Ferramentas da sessão, da mais usada para a menos (empate: pelo nome). */
export function ferramentasOrdenadas(tools: Record<string, number> | undefined): Array<[string, number]> {
  return Object.entries(tools ?? {})
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

/** Bloco "Estatísticas" do painel do agente. Cada linha tem o seu '—' quando a CLI não informou. */
export function estatisticasHtml(agent: Agent): string {
  const ordenadas = ferramentasOrdenadas(agent.tools);
  const total = ordenadas.reduce((soma, [, n]) => soma + n, 0);
  const lista = ordenadas
    .slice(0, FERRAMENTAS_NA_LISTA)
    .map(([nome, n]) => `<li><b>${escape(nome)}</b> ×${n}</li>`)
    .join('');
  return `<section class="det-stats" aria-labelledby="det-h-stats">
    <h4 id="det-h-stats">${t('det.estatisticas')}</h4>
    <dl class="stats-lista">
      <div><dt>${t('stats.ferramentas')}</dt><dd>${total ? plural('stats.chamadas', total) : '—'}${lista ? `<ul class="stats-tools">${lista}</ul>` : ''}</dd></div>
      <div><dt>${t('stats.entrada')}</dt><dd>${tokensTexto(agent.tokensIn)}</dd></div>
      <div><dt>${t('stats.saida')}</dt><dd>${tokensTexto(agent.tokensOut)}</dd></div>
      <div><dt>${t('stats.custo')}</dt><dd title="${escape(agent.costUsd === undefined ? t('stats.custo_sem') : '')}">${custoTexto(agent.costUsd)}</dd></div>
      <div><dt>${t('stats.linhas')}</dt><dd>${linhasTexto(agent.linesAdded, agent.linesRemoved)}</dd></div>
      <div><dt>${t('stats.modelo')}</dt><dd>${escape(agent.model ?? '—')}</dd></div>
      <div><dt>${t('stats.branch')}</dt><dd>${escape(agent.branch ?? '—')}</dd></div>
    </dl>
  </section>`;
}

const escape = (valor: string): string => valor.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);
